import React, { useEffect, useState } from "react";
import { Mic2 } from "lucide-react";

/**
 * MicInputSelector — dropdown listing available audio-input devices so the
 * host can pipe in a physical mixer / audio interface / bluetooth mic.
 *
 * Uses navigator.mediaDevices.enumerateDevices() which only returns labels
 * AFTER the user has granted mic permission at least once — we handle the
 * "labels are hidden" state with a generic "Input N" placeholder and a
 * "Grant access" button that does a throw-away getUserMedia call so labels
 * show up on the second query.
 */
export default function MicInputSelector({ value, onChange }) {
  const [devices, setDevices] = useState([]);
  const [labelsHidden, setLabelsHidden] = useState(false);
  const [loading, setLoading] = useState(false);

  const refresh = async () => {
    try {
      const list = await navigator.mediaDevices.enumerateDevices();
      const mics = list.filter((d) => d.kind === "audioinput");
      setDevices(mics);
      setLabelsHidden(mics.some((d) => !d.label));
    } catch {
      setDevices([]);
    }
  };

  useEffect(() => {
    refresh();
    const onChg = () => refresh();
    try { navigator.mediaDevices.addEventListener?.("devicechange", onChg); } catch {}
    return () => {
      try { navigator.mediaDevices.removeEventListener?.("devicechange", onChg); } catch {}
    };
  }, []);

  const grantAccess = async () => {
    setLoading(true);
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      // Immediately release so the tap-to-talk flow can re-request with the chosen deviceId
      stream.getTracks().forEach((t) => t.stop());
      await refresh();
    } catch {
      // denied — keep the fallback selector visible
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="radio-host-mic-device" className="flex items-center gap-1.5">
      <Mic2 className="w-3.5 h-3.5 text-white/60 shrink-0" />
      <select
        data-testid="radio-host-mic-device-select"
        value={value || ""}
        onChange={(e) => onChange(e.target.value)}
        title="Pick the audio input your physical mixer / interface / mic is on"
        className="bg-black/40 border border-white/10 rounded px-2 py-2 text-white text-xs focus:border-tv-accent outline-none max-w-[200px] truncate"
      >
        <option value="">Default input</option>
        {devices.map((d, i) => (
          <option key={d.deviceId || i} value={d.deviceId}>
            {d.label || `Input ${i + 1}`}
          </option>
        ))}
      </select>
      {labelsHidden && (
        <button
          data-testid="radio-host-mic-device-grant"
          onClick={grantAccess}
          disabled={loading}
          className="bg-white/10 hover:bg-white/20 text-white/70 text-[10px] font-black px-2 py-1 rounded"
          title="Browsers hide device labels until you grant mic access once"
        >
          {loading ? "…" : "Show"}
        </button>
      )}
    </div>
  );
}
